import type { TimerState } from '@/shared/stores/timerStore'

const STORAGE_KEY = 'timer-state'

export interface PersistedTimerState {
  status: TimerState['status']
  projectId: string | null
  taskId: string | null
  description: string
  tagIds: string[]
  elapsedSeconds: number
  startedAt: string | null
  pausedAt: string | null
  isMinimized: boolean
  timestamp?: number
}

export const timerPersistence = {
  save(state: PersistedTimerState): void {
    try {
      const data: PersistedTimerState = {
        ...state,
        timestamp: Date.now(),
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
    } catch (error) {
      console.error('Failed to save timer state:', error)
    }
  },
  
  load(): PersistedTimerState | null {
    try {
      const data = localStorage.getItem(STORAGE_KEY)
      if (!data) return null
      
      const parsed = JSON.parse(data) as PersistedTimerState
      
      // Calculate elapsed time if timer was running
      if (parsed.status === 'running' && parsed.startedAt) {
        const now = Date.now()
        const start = new Date(parsed.startedAt).getTime()
        const additionalSeconds = Math.floor((now - start) / 1000)
        parsed.elapsedSeconds += additionalSeconds
      }
      
      return parsed
    } catch (error) {
      console.error('Failed to load timer state:', error)
      return null
    }
  },
  
  clear(): void {
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch (error) {
      console.error('Failed to clear timer state:', error)
    }
  },
  
  // For cross-tab synchronization
  subscribe(callback: (state: PersistedTimerState | null) => void): () => void {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        callback(e.newValue ? JSON.parse(e.newValue) : null)
      }
    }
    
    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  },
}

export default timerPersistence
